/* The masterclass, /masterclass (Masterclass.dc.html), inside the product frame in POYT's look
   (product.css .pp--poyt). Every colour is a token (tokens.css). At laptop width the board's 1000px
   video column and the cards beside it; between a laptop and a phone the cards go under the video,
   side by side, and the offer's price box under its words; on a phone (no board: PhoneWeek and the
   watch page's own phone layout as the guide) everything stacks in the board's order. */
.mc {
  display: flex; flex-direction: column; gap: 32px; min-width: 0;
  --mc-cols: minmax(0, 1000fr) minmax(240px, 280fr);
}

/* The heading */
.mc-head { display: flex; flex-direction: column; gap: 12px; }
.mc-eyebrow { font-size: 13px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: var(--poyt-soft); }
.mc-h1 {
  margin: 0; font-family: var(--poyt-heading-font); font-weight: 800; font-size: 52px; line-height: 1.05;
  letter-spacing: -0.015em; color: var(--poyt-ink);
}
.mc-lede { margin: 0; font-size: 18px; line-height: 1.55; color: var(--poyt-soft); }

/* The video and the cards beside it */
.mc-main { display: grid; grid-template-columns: var(--mc-cols); gap: 32px; align-items: start; }
.mc-video-col { display: flex; flex-direction: column; gap: 14px; min-width: 0; container-type: inline-size; }
.mc-video {
  position: relative; width: 100%; aspect-ratio: 1000 / 562; border-radius: 18px; overflow: hidden;
  background: var(--poyt-orange);
}
.mc-video--on { background: var(--player-bg); }
.mc-video-player { position: absolute; inset: 0; }
/* the shared player's own black box stays mounted under the cover, unseen, until Play */
.mc-video:not(.mc-video--on) .mc-video-player { visibility: hidden; }
.mc-video .pp-player { width: 100%; height: 100%; aspect-ratio: auto; border: none; border-radius: 0; }

/* The cover, until Play: the board's orange card */
.mc-cover {
  position: absolute; inset: 0; z-index: 1; border: none; border-radius: 0; background: var(--poyt-orange);
  color: var(--on-dark); text-align: left; display: flex; flex-direction: column; justify-content: space-between;
  align-items: flex-start; padding: clamp(18px, 3.9vw, 56px); padding: clamp(18px, 5.6cqw, 56px); cursor: pointer;
}
/* inside the cover, since the video box clips anything outside it */
.mc-cover:focus-visible { outline: 3px solid var(--on-dark); outline-offset: -8px; }
.mc-cover-eyebrow {
  font-size: clamp(11px, 0.9vw, 13px); font-size: clamp(11px, 1.3cqw, 13px); font-weight: 600; letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--on-dark-86);
}
.mc-cover-foot { display: flex; align-items: flex-end; justify-content: space-between; width: 100%; gap: clamp(14px, 3.2cqw, 32px); }
.mc-cover-words { display: flex; flex-direction: column; gap: clamp(6px, 1.4cqw, 14px); min-width: 0; }
.mc-cover-title {
  font-family: var(--poyt-heading-font); font-weight: 800; font-size: clamp(24px, 4.2vw, 60px);
  font-size: clamp(24px, 6cqw, 60px); line-height: 1; letter-spacing: -0.015em;
}
.mc-cover-sub { font-size: clamp(13px, 1.4vw, 20px); font-size: clamp(13px, 2cqw, 20px); line-height: 1.5; color: var(--on-dark-92); }
.mc-disc {
  width: clamp(56px, 7.8vw, 112px); height: clamp(56px, 7.8vw, 112px);
  width: clamp(56px, 11.2cqw, 112px); height: clamp(56px, 11.2cqw, 112px); border-radius: 50%;
  background: var(--on-dark); color: var(--poyt-orange); display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; transition: transform 0.15s;
}
.mc-disc svg { width: 40%; height: 40%; fill: currentColor; }
.mc-cover:hover .mc-disc { transform: scale(1.05); }
.mc-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

/* Under the video: how far, carry on, start again */
.mc-line { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; font-size: 15px; color: var(--poyt-soft); }
.mc-line-progress { display: flex; align-items: center; gap: 10px; }
.mc-line-actions { display: flex; align-items: center; gap: 20px; }
.mc-bar { width: 120px; height: 5px; border-radius: 3px; background: var(--poyt-rule); display: inline-block; overflow: hidden; }
.mc-bar span { display: block; height: 5px; border-radius: 3px; background: var(--poyt-orange); }
.mc-dot { width: 4px; height: 4px; border-radius: 2px; background: var(--poyt-soft-on-navy); flex-shrink: 0; }
.mc-carry { border: none; background: none; padding: 0; color: var(--poyt-orange); font-weight: 600; font-size: 15px; }
.mc-restart { border: none; background: none; padding: 0; color: var(--poyt-soft); font-weight: 500; font-size: 15px; text-decoration: underline; }
.mc-carry:hover, .mc-restart:hover { color: var(--poyt-ink); }
.mc-sideways { margin: 0; font-size: 13px; color: var(--poyt-soft); }
.mc-disclaimer { margin: 4px 0 0; font-size: 13px; line-height: 1.55; color: var(--poyt-soft); max-width: 72ch; }

/* The 2 cards beside the video */
.mc-side { display: flex; flex-direction: column; gap: 20px; min-width: 0; }
.mc-card {
  padding: 26px; border-radius: 16px; background: var(--shell-bg); border: 1px solid var(--poyt-rule);
  display: flex; flex-direction: column; gap: 10px; min-width: 0;
}
.mc-card-head { font-size: 12px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: var(--poyt-soft); }
.mc-card-pct { display: flex; align-items: baseline; gap: 8px; }
.mc-card-big { font-family: var(--poyt-heading-font); font-weight: 800; font-size: 44px; line-height: 1; color: var(--poyt-ink); }
.mc-card-word { font-size: 16px; color: var(--poyt-soft); }
.mc-card-bar { height: 6px; border-radius: 3px; background: var(--poyt-rule); overflow: hidden; }
.mc-card-bar span { display: block; height: 6px; border-radius: 3px; background: var(--poyt-orange); }
.mc-card-line { font-size: 15px; color: var(--poyt-ink); }
.mc-card-text { margin: 0; font-size: 15px; line-height: 1.6; color: var(--poyt-ink); overflow-wrap: anywhere; }

/* The placeholder and the owner's card sit under the video column, at its width */
.mc-under { display: grid; grid-template-columns: var(--mc-cols); column-gap: 32px; }
.mc-under > * { grid-column: 1; }
.mc-placeholder {
  padding: 28px 32px; border-radius: 16px; border: 1.5px dashed var(--mc-dash);
  display: flex; align-items: center; justify-content: space-between; gap: 24px;
}
.mc-placeholder-text { font-size: 17px; line-height: 1.5; color: var(--poyt-ink); }
.mc-show-now {
  flex-shrink: 0; border: none; background: none; padding: 0; color: var(--poyt-soft); font-size: 14px;
  text-decoration: underline; text-align: left;
}
.mc-show-now:hover:not(:disabled) { color: var(--poyt-ink); }
.mc-show-now:disabled { cursor: not-allowed; }
.mc-owned {
  padding: 32px; border-radius: 16px; background: var(--shell-bg); border: 1px solid var(--poyt-rule);
  display: flex; align-items: center; justify-content: space-between; gap: 24px;
}
.mc-owned-text { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.mc-owned-title { margin: 0; font-family: var(--poyt-heading-font); font-weight: 800; font-size: 26px; line-height: 1.2; color: var(--poyt-ink); }
.mc-owned-line { margin: 0; font-size: 16px; line-height: 1.5; color: var(--poyt-soft); }
.mc-owned-btn {
  flex-shrink: 0; height: 52px; padding: 0 24px; border-radius: 10px; background: var(--poyt-navy);
  font-weight: 600; font-size: 16px; display: flex; align-items: center; justify-content: center; text-decoration: none;
}
.pp .mc-owned-btn { color: var(--on-dark); }

/* The offer: the navy section and its price box */
.mc-offer {
  border-radius: 22px; background: var(--poyt-navy); color: var(--on-dark); padding: 56px;
  display: flex; gap: 56px; min-width: 0;
}
.mc-offer-main { flex-grow: 1; display: flex; flex-direction: column; gap: 22px; min-width: 0; }
.mc-offer-eyebrow { font-size: 13px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: var(--poyt-soft-on-navy); }
.mc-offer-h2 {
  margin: 0; font-family: var(--poyt-heading-font); font-weight: 800; font-size: 50px; line-height: 1.05;
  letter-spacing: -0.015em;
}
.mc-mark { position: relative; display: inline-block; }
.mc-mark::after {
  content: ''; position: absolute; left: -2px; right: -6px; bottom: -6px; height: 5px;
  background: var(--poyt-orange-2); transform: skewX(-12deg);
}
.mc-offer-body { margin: 0; font-size: 18px; line-height: 1.6; color: var(--on-dark-88); max-width: 640px; }
.mc-stack { list-style: none; margin: 4px 0 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
.mc-stack-item { display: flex; gap: 14px; align-items: flex-start; font-size: 17px; line-height: 1.5; }
.mc-tick { flex-shrink: 0; margin-top: 2px; fill: none; stroke: var(--poyt-orange-2); stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; }
.mc-offer-timing { margin: 6px 0 0; font-size: 16px; color: var(--poyt-soft-on-navy); }
.mc-price {
  width: 400px; flex-shrink: 0; padding: 36px; border-radius: 18px; background: var(--poyt-navy-2);
  display: flex; flex-direction: column; gap: 14px; align-self: flex-start;
}
.mc-price-was { font-size: 18px; color: var(--poyt-soft-on-navy); text-decoration: line-through; }
.mc-price-public { font-size: 15px; color: var(--poyt-soft-on-navy); }
.mc-price-now { display: flex; align-items: baseline; gap: 10px; }
.mc-price-big { font-family: var(--poyt-heading-font); font-weight: 800; font-size: 60px; line-height: 1; }
.mc-price-small { font-size: 20px; font-weight: 600; }
.mc-price-note { font-size: 16px; color: var(--on-dark-88); margin-top: -4px; }
.mc-join, .mc-secure {
  display: flex; align-items: center; justify-content: center; text-align: center; border-radius: 12px;
  font-weight: 600; text-decoration: none; padding: 0 16px;
}
.mc-join { height: 58px; background: var(--poyt-orange-2); font-size: 19px; margin-top: 8px; }
.mc-secure { height: 54px; border: 1px solid var(--on-dark-45); background: transparent; font-size: 17px; }
.pp .mc-join, .pp .mc-secure { color: var(--on-dark); }
.mc-secure:hover { background: var(--on-dark-08); }
.mc-join:focus-visible, .mc-secure:focus-visible { outline: 2px solid var(--on-dark); outline-offset: 2px; }
.mc-price-balance { font-size: 14px; line-height: 1.5; color: var(--poyt-soft-on-navy); }

/* Between a laptop and a phone: the cards under the video, side by side; the price box under the words */
@media (max-width: 1099px) {
  .mc { --mc-cols: minmax(0, 1fr); }
  .mc-main { gap: 24px; }
  .mc-side { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
  .mc-offer { flex-direction: column; gap: 36px; padding: 44px 40px; }
  .mc-price { width: min(400px, 100%); }
}

/* A phone (no board draws it): the board's order, stacked */
@media (max-width: 760px) {
  .mc { gap: 22px; }
  .mc-head { gap: 8px; }
  .mc-eyebrow { font-size: 12px; letter-spacing: 0.14em; }
  .mc-h1 { font-size: 32px; line-height: 1.08; }
  .mc-lede { font-size: 16px; line-height: 1.5; }
  .mc-main { gap: 20px; }
  .mc-video-col { gap: 10px; }
  .mc-video { border-radius: 12px; }
  .mc-line { flex-direction: column; align-items: flex-start; gap: 10px; }
  .mc-line > .mc-dot { display: none; }
  .mc-line-actions { gap: 14px; }
  .mc-disclaimer { font-size: 12px; }
  .mc-side { display: flex; flex-direction: column; gap: 14px; }
  .mc-card { padding: 20px; }
  .mc-card-big { font-size: 36px; }
  .mc-placeholder { flex-direction: column; align-items: flex-start; gap: 12px; padding: 20px; }
  .mc-placeholder-text { font-size: 16px; }
  .mc-owned { flex-direction: column; align-items: stretch; gap: 18px; padding: 22px 20px; }
  .mc-owned-title { font-size: 22px; }
  .mc-offer { border-radius: 16px; padding: 28px 20px; gap: 28px; }
  .mc-offer-main { gap: 18px; }
  .mc-offer-eyebrow { font-size: 12px; letter-spacing: 0.14em; }
  .mc-offer-h2 { font-size: 34px; line-height: 1.08; }
  .mc-offer-body { font-size: 16px; }
  .mc-stack-item { font-size: 16px; gap: 12px; }
  .mc-offer-timing { font-size: 15px; }
  .mc-price { width: 100%; padding: 24px 20px; gap: 12px; border-radius: 14px; }
  .mc-price-big { font-size: 48px; }
  .mc-price-small { font-size: 18px; }
  /* a button's words may take 2 lines at the narrowest widths */
  .mc-join, .mc-secure { height: auto; min-height: 54px; padding: 10px 16px; line-height: 1.3; }
  .mc-join { font-size: 18px; }
  .mc-secure { min-height: 50px; font-size: 16px; }
}
